import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export type AnalyticsEventType =
  | 'page_view'
  | 'product_view'
  | 'add_to_bag'
  | 'checkout_started'
  | 'session_end';

interface AnalyticsEvent {
  type: AnalyticsEventType;
  businessId: string;
  productId?: string;
  shopperId?: string;
  metadata?: Record<string, any>;
  timestamp: Timestamp;
}

const COLLECTION = 'analytics_events';

// Debounce duplicate events within a short window (same type+target within 5s)
const recentEvents = new Map<string, number>();
const DEBOUNCE_MS = 5000;

function isDuplicate(key: string): boolean {
  const last = recentEvents.get(key);
  if (last && Date.now() - last < DEBOUNCE_MS) return true;
  recentEvents.set(key, Date.now());
  // Prune old entries periodically
  if (recentEvents.size > 100) {
    const now = Date.now();
    for (const [k, v] of recentEvents) {
      if (now - v > DEBOUNCE_MS * 2) recentEvents.delete(k);
    }
  }
  return false;
}

export function logAnalyticsEvent(
  type: AnalyticsEventType,
  businessId: string,
  options?: {
    productId?: string;
    shopperId?: string;
    metadata?: Record<string, any>;
  }
): void {
  if (!businessId) return;

  if (type !== 'session_end') {
    const dedupeKey = `${type}:${businessId}:${options?.productId || ''}`;
    if (isDuplicate(dedupeKey)) return;
  }

  const event: Record<string, any> = {
    type,
    businessId,
    timestamp: Timestamp.now(),
  };
  if (options?.productId) event.productId = options.productId;
  if (options?.shopperId) event.shopperId = options.shopperId;
  if (options?.metadata) event.metadata = options.metadata;

  addDoc(collection(db, COLLECTION), event).catch(err => {
    console.error('Failed to log analytics event:', err);
  });
}
